import * as React from 'react';
import { useLocation } from 'react-router';

import { ApolloCache, ApolloError, useMutation } from '@apollo/client';
import { uniq } from 'lodash';

import { Toaster } from '@notino/react-styleguide';
import {
  GetItemDataByIdQuery,
  WishlistList,
  WishlistMutateListsMutation,
  WishlistMutateListsMutationVariables,
  VtoProductBasic,
} from '@notino/shared/definitions/types';
import { AdditionalServicesAvailability } from '@notino/web-tracking';

import getItemDataByIdQuery from '@components/Universals/ProductItem/queries/itemDataById.graphql';
import { useIsSkinAnalyzerVisible } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/SkinAnalyser/hooks/useIsSkinAnalyzerVisible';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { useWishlistId } from '@containers/WishList/hooks/useWishlistId';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { useTrackingContext } from '@context/tracking/TrackingContext';

import wishlistMutateListsMutation from '../queries/wishlistMutateLists.graphql';
import { trackExponeaWishlistUpdate } from '../tracking/exponea';
import {
  handleTrackWishlistOperation,
  WishlistActionLocation,
} from '../tracking/gtm';
import { updateWishlistedCache } from '../updateWishlistedCache';
import { useWishlistLists } from '../useWishlistLists';
import { getWishlistErrorToastProps } from '../useWishlistSelectionDrawer/utils';

export const publishUpdateWishlistCountEvent = () => {
  const event = new CustomEvent('header:wishlist:overview:update');
  document.dispatchEvent(event);
};

interface IUseWishlistActions {
  productId: string;
  onCompleted?: (
    products: { productId: string; wishlisted: boolean }[]
  ) => void;
  selectionMode?: boolean;
}

export const useWishlistActionsFactory = ({
  onCompleted,
  selectionMode,
}: Pick<IUseWishlistActions, 'onCompleted' | 'selectionMode'>) => {
  const { getTimeFromInit } = useTrackingContext();
  const pdContext = useProductDetailContext(false);
  const flags = useFeatureFlags();
  const shouldShowSkinAnalyzer = useIsSkinAnalyzerVisible();
  const { listId } = useWishlistId();
  const { pathname } = useLocation();
  const isWishlistPage = pathname.includes('/wishlist');

  const [mutateLists, { loading }] = useMutation<
    WishlistMutateListsMutation,
    WishlistMutateListsMutationVariables
  >(wishlistMutateListsMutation);

  const handleWishlistClick = React.useCallback(
    async ({
      productIds,
      addToLists,
      removeFromLists,
      lists,
      actionLocation,
      trackingPromoLabels = [],
      listName,
      listPosition,
      doNotTrack,
      interaction = 'product_add',
      notifyFailed,
    }: {
      productIds: string[];
      addToLists: { id: string }[];
      removeFromLists: { id: string }[];
      lists: WishlistList[];
      actionLocation: WishlistActionLocation;
      trackingPromoLabels?: string[];
      listName?: string;
      listPosition?: number;
      doNotTrack?: boolean;
      interaction?: 'product_add' | 'remove_button' | 'move_button';
      notifyFailed?: boolean;
    }) => {
      const productId = productIds[0];

      const wishlisted = isWishlisted({
        addToLists,
        removeFromLists,
        lists,
        productId,
      });

      const productsWishlisted = productIds.map((id) => ({
        productId: id,
        wishlisted: isWishlisted({
          productId: id,
          addToLists,
          removeFromLists,
          lists,
        }),
      }));

      try {
        const { data } = await mutateLists({
          variables: {
            productIds,
            addToList: addToLists.map((list) => list.id),
            removeFromList: removeFromLists.map((list) => list.id),
          },
          update: (cache: ApolloCache<object>) => {
            updateWishlistedCache({
              cache,
              products: productsWishlisted,
            });

            const vpProducts = productIds
              .map((id) => {
                return cache.readQuery<GetItemDataByIdQuery>({
                  query: getItemDataByIdQuery,
                  variables: { id },
                })?.vpProductById;
              })
              .filter((x): x is NonNullable<typeof x> => x != null);

            const vtoProducts: VtoProductBasic[] = [];
            try {
              const cacheData = cache.extract();
              Object.keys(cacheData).forEach((key) => {
                if (key.startsWith('VtoProductBasic:')) {
                  const vtoProduct = cacheData[key] as VtoProductBasic;
                  if (
                    vtoProduct &&
                    productIds.includes(String(vtoProduct.webProductId))
                  ) {
                    vtoProducts.push(vtoProduct);
                  }
                }
              });
            } catch (error) {
              // Silently fail if cache reading fails
            }

            const isCurrentProductDetail =
              pdContext?.currentVariant.webId === productId;

            if (
              vpProducts.length > 0 ||
              vtoProducts.length > 0 ||
              isCurrentProductDetail
            ) {
              if (doNotTrack) {
                return;
              }
              handleTrackWishlistOperation({
                lists,
                productCount: productIds.length,
                addToLists,
                removeFromLists,
                wishlisted,
                shouldShowSkinAnalyzer,
                selectionMode,
                vpProducts,
                vtoProducts,
                timing: getTimeFromInit(),
                flags,
                actionLocation,
                interaction,
                additionalPromoLabels: trackingPromoLabels,
                product: isCurrentProductDetail ? pdContext.product : undefined,
                variant: isCurrentProductDetail
                  ? pdContext?.currentVariant
                  : undefined,
                additionalGtm: {
                  list_name: listName,
                  list_position: listPosition,
                  ...(isWishlistPage && {
                    wishlist_type:
                      listId === useWishlistLists.DEFAULT_LIST_ID
                        ? 'default'
                        : 'custom',
                  }),
                },
                engravingAvailable:
                  pdContext?.engravingAvailable ??
                  AdditionalServicesAvailability.notAvailable,
                tryItFirstAvailable:
                  pdContext?.tryItFirstAvailable ??
                  AdditionalServicesAvailability.notAvailable,
              });
            }
          },
        });

        const wishlistProductIds = uniq(
          data?.WishlistMutateLists?.lists?.flatMap((item) => item.products) ||
            []
        );

        trackExponeaWishlistUpdate({
          action: wishlisted ? 'add' : 'remove',
          productId: Number(productId),
          wishlistProductIds: wishlistProductIds.map(Number),
        });

        publishUpdateWishlistCountEvent();
        onCompleted?.(productsWishlisted);
      } catch (error) {
        if (notifyFailed) {
          const toastProps = getWishlistErrorToastProps(
            error as ApolloError,
            lists
          );
          if (toastProps) {
            Toaster.error(toastProps);
          }
        }
        throw error;
      }
    },
    [
      mutateLists,
      onCompleted,
      getTimeFromInit,
      pdContext,
      flags,
      shouldShowSkinAnalyzer,
      isWishlistPage,
      listId,
      selectionMode,
    ]
  );

  return React.useMemo(
    () => ({
      handleWishlistClick,
      loading,
    }),
    [handleWishlistClick, loading]
  );
};

export const useWishlistActions = ({
  productId,
  onCompleted,
}: IUseWishlistActions) => {
  const { handleWishlistClick, loading } = useWishlistActionsFactory({
    onCompleted,
  });

  return React.useMemo(
    () => ({
      handleWishlistClick: ({
        actionLocation,
        trackingPromoLabels = [],
        addToLists,
        removeFromLists,
        lists,
        notifyFailed,
      }: {
        actionLocation: WishlistActionLocation;
        trackingPromoLabels?: string[];
        addToLists: { id: string }[];
        removeFromLists: { id: string }[];
        lists: WishlistList[];
        notifyFailed?: boolean;
      }) => {
        return handleWishlistClick({
          productIds: [productId],
          addToLists,
          removeFromLists,
          lists,
          actionLocation,
          trackingPromoLabels,
          notifyFailed,
        });
      },
      loading,
    }),
    [handleWishlistClick, loading, productId]
  );
};

const isWishlisted = ({
  addToLists,
  removeFromLists,
  lists,
  productId,
}: {
  addToLists: { id: string }[];
  removeFromLists: { id: string }[];
  lists: WishlistList[];
  productId: string;
}) => {
  if (addToLists.length) {
    return true;
  }
  const wasWishlistedInListIds = lists
    .filter((list) => list.products.includes(Number(productId)))
    .map((list) => list.id);

  const areAllRemoved = wasWishlistedInListIds.every((listId) =>
    removeFromLists.some(({ id }) => id === listId)
  );

  return !areAllRemoved;
};
